import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import usePageBackground from "../hooks/usePageBackground";

// Shared auth surface, styled to match the landing page: near-black page, a dimmed
// collage of real member hauls, a purple glow from the top, a floating pill header
// and a white rounded card. Used by every public auth page (sign-in, register,
// forgot password, reset password, verify) and the paywall.

const PAGE_BG = "#050505";
const MINIMAL_BG = "#0B0B0D";

// Onest is loaded in index.html (same face as the landing page).
const AUTH_SANS = "font-[Onest,system-ui,sans-serif]";

// Kept for OnboardingPage, which still uses the rounded display face.
export const ROUNDED_DISPLAY =
  "font-[Manrope,Nunito,'SF_Pro_Rounded','ui-rounded',Inter,system-ui,sans-serif]";

// Card text tokens
export const CARD_TEXT = "text-[#18181B]";
export const CARD_SUBTLE = "text-[#71717A]";

export const PINTO_INPUT_CLASS =
  "!rounded-xl !border !border-[#E4E4E7] !bg-[#F4F4F5] hover:!bg-[#EFEFF1] focus:!bg-white focus:!border-[#7C3AED] focus:!ring-4 focus:!ring-[#7C3AED]/15 !text-[#18181B] placeholder:!text-[#A1A1AA] !py-3.5 !px-4 !text-[15px] !transition-all";

// Primary purple button — used for every "Continue / Sign in / Send code" CTA.
export const PRIMARY_BTN_CLASS =
  "!rounded-[13px] !py-3.5 !text-base !font-medium !bg-[#7C3AED] hover:!bg-[#6D28D9] !text-white !shadow-[0_10px_30px_rgba(124,58,237,0.35)] active:!scale-[0.98] !transition-all";

export const PINTO_LINK_CLASS =
  "font-medium text-[#7C3AED] hover:text-[#6D28D9] transition-colors";

// Real community-success hauls (same photos as the landing hero).
const HAULS = [
  "penny-milwaukee-cart", "penny-vacuums", "penny-hoses", "penny-shark-stainstriker", "carote-cookware",
  "blackstone-griddle", "penny-ladders", "backpacks", "coolers", "penny-fire-pit", "shark-stratos", "penny-headphones",
];

function AuthBackground() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -inset-10 grid rotate-[-4deg] scale-110 grid-cols-3 gap-3 opacity-35 sm:grid-cols-6">
        {HAULS.map((name, i) => (
          <img
            key={name}
            src={`/landing/community/${name}.webp`}
            alt=""
            decoding="async"
            className={`block w-full rounded-[18px] object-cover ${i % 3 === 0 ? "h-[300px]" : "h-[220px]"} ${
              (i % 6) % 2 ? "mt-[60px]" : ""
            } ${i >= 6 ? "hidden sm:block" : ""}`}
          />
        ))}
      </div>
      <div className="absolute inset-0 bg-[radial-gradient(45%_55%_at_50%_50%,rgba(5,5,5,0.7)_0%,rgba(5,5,5,0.94)_75%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(50%_40%_at_50%_0%,rgba(124,58,237,0.45)_0%,rgba(124,58,237,0)_100%)]" />
    </div>
  );
}

interface PintoAuthShellProps {
  children: ReactNode;
  /** Optional small footer content rendered below the card (e.g. terms / support links). */
  footer?: ReactNode;
  /** Optional control pinned to the viewport (e.g. Log out on verify). */
  topRight?: ReactNode;
  /** Hide the home back arrow; keep the logo. */
  hideBack?: boolean;
  /** Slightly wider card for multi-step flows like the paywall. */
  wide?: boolean;
  /** Extra classes on the white card. */
  cardClassName?: string;
  /** Shotbase-style sign-in: no card or photo backdrop, one narrow column on flat dark. */
  minimal?: boolean;
}

function AuthHeader({ hideBack }: { hideBack?: boolean }) {
  return (
    <div className="flex h-12 items-center gap-3 rounded-2xl border border-[#27272A] bg-[#18181B]/85 py-1.5 pl-1.5 pr-4 text-[#FAFAFA] shadow-[0_10px_30px_rgba(0,0,0,0.4)] backdrop-blur-md">
      {!hideBack && (
        <Link
          to="/"
          aria-label="Back to home"
          className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[#D4D4D8] transition-colors hover:bg-white/10 hover:text-white"
        >
          <ArrowLeft size={18} strokeWidth={2} />
        </Link>
      )}
      <Link to="/" className={`flex items-center gap-2.5 transition-opacity hover:opacity-85 ${hideBack ? "pl-1" : ""}`}>
        <img src="/app_icon.png" alt="" className="h-[30px] w-[30px] rounded-lg" />
        <span className="text-[15px] font-semibold">DealHawk</span>
      </Link>
    </div>
  );
}

export default function PintoAuthShell({
  children,
  footer,
  topRight,
  hideBack,
  wide,
  cardClassName,
  minimal,
}: PintoAuthShellProps) {
  // Match the iOS Safari overscroll / safe-area regions to the dark page.
  usePageBackground(minimal ? MINIMAL_BG : PAGE_BG, minimal ? MINIMAL_BG : PAGE_BG);

  if (minimal) {
    return (
      <div className={`relative flex min-h-screen flex-col items-center bg-[#0B0B0D] px-5 text-[#FAFAFA] ${AUTH_SANS}`}>
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(50%_100%_at_50%_0%,rgba(124,58,237,0.22)_0%,rgba(124,58,237,0)_100%)]" />
        {!hideBack && (
          <Link
            to="/"
            className="absolute left-4 top-5 z-10 inline-flex h-11 items-center gap-2 rounded-xl px-3 text-sm font-medium text-[#A1A1AA] transition-colors hover:bg-white/5 hover:text-white sm:left-6 sm:top-6"
          >
            <ArrowLeft size={16} strokeWidth={2} />
            DealHawk
          </Link>
        )}
        {topRight && <div className="absolute right-6 top-6 z-10">{topRight}</div>}
        <div className="relative flex w-full max-w-[360px] flex-1 flex-col justify-center py-24 animate-rise-in">
          {children}
          {footer && <div className="mt-6 text-center">{footer}</div>}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative flex min-h-screen justify-center overflow-hidden bg-[#050505] px-4 pb-10 ${
        hideBack ? "items-start pt-24" : "items-start pt-24 sm:items-center sm:pb-14"
      } ${AUTH_SANS} ${CARD_TEXT}`}
    >
      <AuthBackground />
      <div className="absolute left-1/2 top-4 z-50 -translate-x-1/2">
        <AuthHeader hideBack={hideBack} />
      </div>
      {topRight && <div className="absolute right-6 top-6 z-50">{topRight}</div>}

      <div className={`relative w-full animate-rise-in ${wide ? "max-w-lg" : "max-w-[460px]"}`}>
        <div
          className={`rounded-[28px] bg-white shadow-[0_40px_100px_rgba(76,29,149,0.45)] ${
            cardClassName || "px-6 py-8 sm:px-10 sm:py-10"
          }`}
        >
          {children}
        </div>

        {footer && <div className="mt-6 px-2 text-center">{footer}</div>}
      </div>
    </div>
  );
}
